import {signalStore, withState, withMethods, withComputed, patchState} from '@ngrx/signals';
import {computed, inject} from '@angular/core';
import {Observable, tap, catchError, of, throwError} from 'rxjs';
import {CartSnapshotService} from '../../shared/services/cart-snapshot.service';
import {ICartSnapshot, ICartSnapshotGrade} from '../../types/ICartSnapshot';
import {ToastService} from '../../shared/services/toast.service';
import {PostHogService} from '../../shared/services/posthog.service';
import {setError, setFulfilled, setPending, withRequestStatus} from '../../shared/store/features/with-request-status';

type SelectionState = {
  snapshot: ICartSnapshot | null;
  selected: 'good' | 'better' | 'best' | null;
  addonsOpen: boolean;
};

export const SelectionStore = signalStore(
  withState<SelectionState>({
    snapshot: null,
    selected: null,
    addonsOpen: false,
  }),
  withRequestStatus('snapshot'),
  withRequestStatus('submit'),
  withComputed((state) => ({
    grades: computed((): Array<{key: 'good' | 'better' | 'best'; data: ICartSnapshotGrade}> => {
      const snapshot = state.snapshot();
      if (!snapshot) return [];
      return (['good', 'better', 'best'] as const)
        .filter((key): key is 'good' | 'better' | 'best' => !!snapshot[key])
        .map(key => ({key, data: snapshot[key] as ICartSnapshotGrade}));
    }),
  })),
  withMethods((store, toastService = inject(ToastService), posthogService = inject(PostHogService)) => {
    const cartSnapshotService = inject(CartSnapshotService);

    return {
      loadSnapshot(token: string): Observable<ICartSnapshot> {
        patchState(store, setPending('snapshot'));
        return cartSnapshotService.show(token).pipe(
          tap(snapshot => patchState(store, setFulfilled('snapshot'), {snapshot})),
          catchError((err: Error) => {
            patchState(store, setError('snapshot', err.message));
            return of(null as unknown as ICartSnapshot);
          }),
        );
      },

      selectGrade(grade: 'good' | 'better' | 'best') {
        patchState(store, {selected: grade});
      },

      setAddonsOpen(open: boolean) {
        patchState(store, {addonsOpen: open});
      },

      submit(token: string, signature: string): Observable<void> {
        const grade = store.selected();
        if (!grade) throw new Error('No grade selected');
        patchState(store, setPending('submit'));
        return cartSnapshotService.submit(token, grade, signature).pipe(
          tap(() => {
            posthogService.posthog.capture('customer_selection_submitted', {
              selected_grade: grade,
            });
            toastService.success('Selection submitted successfully');
            patchState(store, setFulfilled('submit'));
          }),
          catchError((err) => {
            patchState(store, setError('submit', err.message));
            toastService.error(err.message);
            return throwError(() => err);
          })
        );
      },
    };
  }),
);
